/**
 * --------------------------------------------------------------------------
 * Component Name : ExpandableView.jsx
 * Created By     : Sreeraj
 * Description    : Displays month-wise expandable profit reports with
 *                  combined summary and separated B2B & B2C service cards.
 * --------------------------------------------------------------------------
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Collapse,
  Paper,
  Divider,
  Grid,
} from "@mui/material";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import "./ExpandableView.css";

const ExpandableView = ({ apiResponse, showYear = "" }) => {
  const [openMonth, setOpenMonth] = useState(null);
  const [reportData, setReportData] = useState([]);

  const formatMonth = (shortMonth) => {
    const monthNames = {
      Jan: "January",
      Feb: "February",
      Mar: "March",
      Apr: "April",
      May: "May",
      Jun: "June",
      Jul: "July",
      Aug: "August",
      Sep: "September",
      Oct: "October",
      Nov: "November",
      Dec: "December",
    };
    return monthNames[shortMonth] || shortMonth;
  };

  useEffect(() => {
    if (apiResponse && apiResponse.monthly_report) {
      const transformed = apiResponse.monthly_report.map((monthObj) => {
        const { month, services, monthly_total } = monthObj;
        let details = [];

        Object.keys(services || {}).forEach((serviceKey) => {
          const serviceData = services[serviceKey];
          const b2b = serviceData?.B2B || {};
          const b2c = serviceData?.B2C || {};

          details.push({
            service: serviceKey,
            b2bSales: b2b.total_las_margin || 0,
            b2cSales: b2c.total_las_margin || 0,
          });
        });

        return {
          month: formatMonth(month),
          totalSales: monthly_total?.total_las_margin || 0,
          totalProfit: monthly_total?.total_profit || 0,
          totalExpense: monthly_total?.total_expense || 0,
          details,
        };
      });

      setReportData(transformed);
    }
  }, [apiResponse]);

  const handleToggleMonth = (month) => {
    setOpenMonth(openMonth === month ? null : month);
  };

  return (
   
      <Box className="profit-report__container">
        {reportData.map((monthData) => {
          const isOpen = openMonth === monthData.month;
          return (


            <Box
              key={monthData.month}
              className={`profit-report__month-box ${isOpen ? "active" : ""
                }`}
            >
              {/* ---------- Header ---------- */}
              <Box
                className={`profit-report__month-header ${isOpen ? "expanded" : ""
                  }`}
              // onClick={() => handleToggleMonth(monthData.month)}
              >

                <Box className="profit-report__month-info">
                  <Typography className="profit-report__month-title">
                    {monthData.month}
                  </Typography>

                  {/* Combined summary row */}
                  <Box className="profit-report__summary-row">
                    <Typography className="profit-report__summary-text">
                      <strong>LAS Margin:</strong> ₹{monthData.totalSales}
                    </Typography>
                    <Typography className="profit-report__summary-text">
                      <strong>Expense:</strong> ₹{monthData.totalExpense}
                    </Typography>
                    <Typography className="profit-report__summary-text">
                      <strong>Profit:</strong> ₹{monthData.totalProfit}
                    </Typography>
                  </Box>
                </Box>


                {/* 
              <Box className={`profit-report__icon ${isOpen ? "rotate" : ""}`}>
                {isOpen ? <IoChevronUp /> : <IoChevronDown />}
              </Box> */}
              </Box>

              {/* ---------- Expandable Details ---------- */}
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <Box className="profit-report__details-container">
                  {monthData.details.length > 0 ? (
                    <Grid container spacing={2}>
                      {monthData.details.map((detail, index) => (
                        <Grid item xs={12} md={6} lg={4} key={index}>
                          <Paper
                            className="profit-report__service-card"
                            elevation={2}
                          >
                            <Typography className="profit-report__service-name">
                              {detail.service}
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Box className="profit-report__b2-section">
                              <Typography className="profit-report__b2b-title">
                                B2B
                              </Typography>
                              <Typography className="profit-report__b2b-value">
                                ₹{detail.b2bSales}
                              </Typography>
                            </Box>
                            <Box className="profit-report__b2-section">
                              <Typography className="profit-report__b2c-title">
                                B2C
                              </Typography>
                              <Typography className="profit-report__b2c-value">
                                ₹{detail.b2cSales}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography align="center" sx={{ p: 2, fontSize: 14 }}>
                      No data available for this month.
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>


          );
        })}

        {/* ---------- Grand Total ---------- */}
        {apiResponse?.grand_total && (
          <Paper className="profit-report__grand-card" elevation={3}>
            <Typography variant="h6" className="profit-report__grand-title">
              {showYear} Summary
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Box className="profit-report__grand-values">
              <Typography>
                <strong>Margin:</strong> ₹
                {apiResponse.grand_total.total_las_margin}
              </Typography>
              <Typography>
                <strong>Expense:</strong> ₹
                {apiResponse.grand_total.total_expense}
              </Typography>
              <Typography>
                <strong>Profit:</strong> ₹
                {apiResponse.grand_total.total_profit}
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>

  );
};

export default ExpandableView;
